import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { VariableSizeList as List } from 'react-window';
import ShoppingListRow from './ShoppingListRow';
import { ShoppingItemWithProduct, CategoryInfo } from '@/types';
import { useCategoryOrdering, useShops } from '@/hooks/useShops';
import { useThemeConfig } from '@/components/ui/theme-config-provider';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface VirtualizedCategoryListViewProps {
  listId: string;
  items: ShoppingItemWithProduct[];
  categories: CategoryInfo[];
  onCheckItem: (id: string, checked: boolean) => void;
  onDeleteItem: (id: string) => void;
  onEditItem: (item: ShoppingItemWithProduct) => void;
  rapidInsert: boolean;
  shopId?: string;
  height: number;
}

type RowEntry =
  | { type: 'category'; category: CategoryInfo }
  | { type: 'item'; item: ShoppingItemWithProduct };

const HEADER_HEIGHT = 40;
const ITEM_HEIGHT = 36;

interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    entries: RowEntry[];
    onToggle: (item: ShoppingItemWithProduct) => void;
    onCheck: (id: string, checked: boolean) => void;
    onDelete: (id: string) => void;
    onEdit: (item: ShoppingItemWithProduct) => void;
    rapidInsert: boolean;
    theme: ReturnType<typeof useThemeConfig>['theme'];
    collapseCategory: (categoryId: string) => void;
    expandCategory: (categoryId: string) => void;
    isCategoryCollapsed: (categoryId: string) => boolean;
  };
}

const Row = React.memo(({ index, style, data }: RowProps) => {
  const entry = data.entries[index];
  if (!entry) return null;

  if (entry.type === 'category') {
    const category = entry.category;
    const isCollapsed = data.isCategoryCollapsed(category.id);
    return (
      <div style={{ ...style, boxSizing: 'border-box' }} key={`header-${category.id}`}>
        <div
          className="px-4 py-1 font-medium flex items-center rounded-t-lg border"
          style={{
            backgroundColor: category.color,
            color: category.textColor || '#000000',
            borderColor: data.theme.borderColor,
          }}
        >
          <span className="mr-2">{category.icon || '📋'}</span>
          {category.name || 'Altri articoli'}
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => data.collapseCategory(category.id)}
              disabled={isCollapsed}
              className="rounded p-1 transition-colors disabled:opacity-50"
              style={{ color: category.textColor || '#000000' }}
              aria-label={`Comprimi categoria ${category.name || 'Altri articoli'}`}
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => data.expandCategory(category.id)}
              disabled={!isCollapsed}
              className="rounded p-1 transition-colors disabled:opacity-50"
              style={{ color: category.textColor || '#000000' }}
              aria-label={`Espandi categoria ${category.name || 'Altri articoli'}`}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const item = entry.item;
  const isLastItem =
    index === data.entries.length - 1 ||
    data.entries[index + 1].type === 'category';
  return (
    <div style={{ ...style, boxSizing: 'border-box' }} key={item.id}>
      <div
        className={`border-x ${isLastItem ? 'border-b rounded-b-lg' : 'border-b'}`}
        style={{
          backgroundColor: data.theme.cardBackgroundColor,
          borderColor: data.theme.borderColor,
        }}
      >
        <ShoppingListRow
          item={item}
          onToggle={data.onToggle}
          onCheck={data.onCheck}
          onEdit={data.onEdit}
          rapidInsert={data.rapidInsert}
        />
      </div>
    </div>
  );
});
Row.displayName = 'Row';

export default function VirtualizedCategoryListView({
  listId,
  items,
  categories,
  onCheckItem,
  onDeleteItem,
  onEditItem,
  rapidInsert,
  shopId,
  height
}: VirtualizedCategoryListViewProps) {
  const { theme } = useThemeConfig();
  const { data: shopOrdering } = useCategoryOrdering(shopId || null);
  // Recupera il nome del negozio per etichettare le categorie nascoste
  const { shops } = useShops();
  const shopName = shops.find((s) => s.id === shopId)?.shop_name;
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set()
  );
  const listRef = useRef<List>(null);

  const collapseCategory = useCallback((categoryId: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      next.add(categoryId);
      return next;
    });
  }, []);

  const expandCategory = useCallback((categoryId: string) => {
    setCollapsedCategories((prev) => {
      if (!prev.has(categoryId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(categoryId);
      return next;
    });
  }, []);

  const isCategoryCollapsed = useCallback(
    (categoryId: string) => collapsedCategories.has(categoryId),
    [collapsedCategories]
  );

  const hiddenCategories = new Set(
    (shopOrdering ?? []).filter((e) => e.visibile === false).map((e) => e.category_id)
  );

  // Ensure categories for fallback
  const categoriesToRenderBase: CategoryInfo[] =
    categories.length > 0
      ? categories
      : [
          {
            id: 'none',
            name: 'Altri articoli',
            color: '#f0f0f0',
            icon: '📋',
            position: 0,
            textColor: '#000000'
          }
        ];

  let categoriesToRender = categoriesToRenderBase.filter(
    (c) => !hiddenCategories.has(c.id)
  );

  const groupedItems: Record<string, ShoppingItemWithProduct[]> = {};
  categoriesToRender.forEach((c) => {
    groupedItems[c.id] = [];
  });
  if (!groupedItems['none']) groupedItems['none'] = [];

  let hasHiddenForShop = false;
  items.forEach((item) => {
    if (hiddenCategories.has(item.category)) {
      hasHiddenForShop = true;
      groupedItems['none'].push(item);
      return;
    }
    const cat = categories.find((c) => (c.categoryId ?? c.id) === item.category);
    if (cat) {
      groupedItems[cat.id].push(item);
    } else {
      groupedItems['none'].push(item);
    }
  });

  const missingCategory: CategoryInfo = {
    id: 'none',
    name: hasHiddenForShop && shopName ? `Categoria nascosta per ${shopName}` : 'Categoria nascosta',
    color: '#f0f0f0',
    icon: '❓',
    position: 999,
    textColor: '#000',
  };

  if (groupedItems['none'].length > 0 && !categoriesToRender.some(c => c.id === 'none')) {
    categoriesToRender.push(missingCategory);
  }

  const orderingMap = new Map((shopOrdering ?? []).map((e) => [e.category_id, e.position]));

  const sortedCategories = [...categoriesToRender]
    .map((c) => ({
      ...c,
      position: orderingMap.has(c.id) ? Number(orderingMap.get(c.id)) : Number(c.position ?? Number.MAX_SAFE_INTEGER)
    }))
    .sort((a, b) => Number(a.position) - Number(b.position));

  const entries: RowEntry[] = useMemo(() => {
    const arr: RowEntry[] = [];
    sortedCategories.forEach((cat) => {
      const catItems = groupedItems[cat.id] || [];
      if (catItems.length === 0) return;
      arr.push({ type: 'category', category: cat });
      if (collapsedCategories.has(cat.id)) {
        return;
      }
      catItems.forEach((it) => arr.push({ type: 'item', item: it }));
    });
    return arr;
  }, [collapsedCategories, groupedItems, sortedCategories]);

  const handleToggle = useCallback(
    (item: ShoppingItemWithProduct) => {
      onCheckItem(item.id, !item.checked);
    },
    [onCheckItem]
  );

  const getItemSize = useCallback(
    (index: number) => (entries[index].type === 'category' ? HEADER_HEIGHT : ITEM_HEIGHT),
    [entries]
  );

  const itemData = useMemo(
    () => ({
      entries,
      onToggle: handleToggle,
      onCheck: onCheckItem,
      onDelete: onDeleteItem,
      onEdit: onEditItem,
      rapidInsert,
      theme,
      collapseCategory,
      expandCategory,
      isCategoryCollapsed
    }),
    [
      collapseCategory,
      entries,
      expandCategory,
      handleToggle,
      isCategoryCollapsed,
      onCheckItem,
      onDeleteItem,
      onEditItem,
      rapidInsert,
      theme
    ]
  );

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [entries]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="ml-[10px]">
      <List
        ref={listRef}
        height={height}
        width="100%"
        itemCount={entries.length}
        itemSize={getItemSize}
        itemData={itemData}
        overscanCount={5}
        className="custom-scrollbar"
      >
        {({ index, style }) => <Row index={index} style={style} data={itemData} />}
      </List>
    </div>
  );
}

